import { apiFetch } from '@/lib/api';
import { downloadBlob, filenameFromContentDisposition } from '@/lib/downloadFile';
import { formatApiErrorBody } from '@/lib/formatApiError';

export interface PlanogramListItem {
  id: string;
  name: string;
  description: string | null;
  slotCount: number;
  slotsFilled: number;
  targetMarginPercent: number;
  blendedMarginPercent: number | null;
  meetsTarget: boolean;
  updatedAt: string;
}

export interface PlanogramSlotProduct {
  id: string;
  sku: string;
  name: string;
  imageUrl: string | null;
  thumbnailUrl?: string | null;
  media?: Array<{ url: string; mediaType: string }> | null;
  purchasePrice: number;
  sellPrice: number;
}

export interface PlanogramSlot {
  id: string;
  slotNumber: number;
  slotLabel: string | null;
  maxCapacity: number;
  sellPriceOverride: number | null;
  listingFee: number;
  productId: string | null;
  product: PlanogramSlotProduct | null;
  effectiveSellPrice: number | null;
  cost: number | null;
  marginPercent: number | null;
}

export interface PlanogramSummary {
  slotCount: number;
  slotsFilled: number;
  targetMarginPercent: number;
  blendedMarginPercent: number | null;
  meetsTarget: boolean;
}

export interface PlanogramDetail {
  id: string;
  name: string;
  description: string | null;
  slotCount: number;
  targetMarginPercent: number;
  monthlyCogs?: number;
  sourceMachineId: string | null;
  createdAt: string;
  updatedAt: string;
  slots: PlanogramSlot[];
  summary: PlanogramSummary;
}

/**
 * Gross margin % (no venue commission). Null when the sell price is unusable OR the product has
 * no purchase cost set — a zero cost means "unknown", not a 100% margin.
 */
export function computeLineMargin(cost: number, sellPrice: number): number | null {
  if (sellPrice <= 0 || cost <= 0) return null;
  return Math.round(((sellPrice - cost) / sellPrice) * 100 * 100) / 100;
}

export { computeBlendedMargin } from '@/lib/planogramIncome';

export type PlanogramExportAudience = 'internal' | 'external';

/** Download a planogram Excel file with embedded product images. */
export async function downloadPlanogramExcel(
  planogramId: string,
  audience: PlanogramExportAudience
): Promise<void> {
  const id = planogramId.trim();
  if (!id) {
    throw new Error('Planogram id is missing');
  }
  const res = await apiFetch(
    `/api/v1/planograms/${encodeURIComponent(id)}/export?audience=${audience}`
  );
  if (!res.ok) {
    throw new Error(formatApiErrorBody(await res.json().catch(() => ({}))));
  }
  const blob = await res.blob();
  const filename = filenameFromContentDisposition(
    res.headers.get('Content-Disposition'),
    `planogram-${audience}.xlsx`
  );
  downloadBlob(filename, blob);
}
